// The outcome of switching the active tenant (BUG-2469): the switch lands only once the server saved
// it, so a caller learns whether it did and, if not, the message to show inline.

import { api } from '../../api-client/typed-client';

/** What a failed tenant switch shows when the server sent no message of its own (BUG-2469). */
export const TENANT_SWITCH_FAILED = "Couldn't switch auction house. Try again.";

/**
 * Outcome of `setCurrentTenant` (BUG-2469). The active tenant changes only on `success: true`.
 * On failure it stays, and `error` is the message to show inline: the server's own, else
 * {@link TENANT_SWITCH_FAILED} (no answer, or offline, where the request cannot be sent).
 */
export type TenantSwitchOutcome = { success: true } | { success: false; error: string };

/**
 * The server's message on a refused tenant switch, else the generic one (BUG-2469).
 *
 * @param res - the non-ok `PUT /api/user/active-tenant` response.
 * @returns the message to show inline.
 */
async function readSwitchError(res: Response): Promise<string> {
    try {
        const body: unknown = await res.json();
        if (body && typeof body === 'object' && 'error' in body && typeof body.error === 'string' && body.error) {
            return body.error;
        }
    } catch {
        // A non-JSON body (a proxy's HTML 502) has no message to show: use the generic one.
    }
    return TENANT_SWITCH_FAILED;
}

/**
 * Ask the server to save the active tenant (`staff_users.last_active_tenant_id`). Never rejects:
 * a refused or unsent request is a failed outcome, logged with its cause.
 *
 * @param tenantId - the house to make active.
 * @returns {@link TenantSwitchOutcome}.
 * @sideEffect Fires `PUT /api/user/active-tenant`.
 */
export async function saveActiveTenant(tenantId: string): Promise<TenantSwitchOutcome> {
    try {
        const res = await api.user.active_tenant.put.raw({ body: { tenantId }, init: { cache: 'no-store' } });
        if (res.ok) return { success: true };
        // eslint-disable-next-line no-console -- client module; createLogger is server-only (ALS). The failure is also surfaced inline by the caller.
        console.warn(
            '[SessionProvider] failed to persist active tenant',
            new Error(`PUT /api/user/active-tenant returned ${res.status}`),
        );
        return { success: false, error: await readSwitchError(res) };
    } catch (err) {
        // eslint-disable-next-line no-console -- client module; createLogger is server-only (ALS). The failure is also surfaced inline by the caller.
        console.warn('[SessionProvider] failed to persist active tenant', err);
        return { success: false, error: TENANT_SWITCH_FAILED };
    }
}
